import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Insert, Tables, Update } from "@/types/database";
import { qk } from "./_queryKeys";
import { assertData, throwIfError } from "./_supabaseErrors";
import { excludeTestData } from "./testData";
import { insertWithWriteSource, type WriteSource } from "./writeSource";
import { isTaskForToday } from "@/lib/todayTasks";
import { indexTaskRow } from "./knowledge";

export type Task = Tables<"tasks">;
export type TaskInsert = Insert<"tasks">;
export type TaskUpdate = Update<"tasks">;
export type TaskTreeRow = Tables<"task_tree_view">;

export async function listTasksByPhase(phaseId: string): Promise<Task[]> {
  const { data, error } = await supabase
    .from("tasks")
    .select("*")
    .eq("phase_id", phaseId)
    .order("order_index", { ascending: true });
  throwIfError(error);
  // deleted_at: 047 után; migráció előtt a mező hiányzik → mind aktív
  return excludeTestData(data).filter((t) => !t.deleted_at);
}

export async function listTaskTree(): Promise<TaskTreeRow[]> {
  const { data, error } = await supabase
    .from("task_tree_view")
    .select("*")
    .order("depth", { ascending: true })
    .order("order_index", { ascending: true });
  throwIfError(error);
  return data ?? [];
}

export type TaskBrief = Pick<
  Tables<"tasks">,
  "id" | "title" | "phase_id" | "status" | "parent_task_id"
>;

export async function listTasksBrief(): Promise<TaskBrief[]> {
  const { data, error } = await supabase.from("tasks").select("*").order("title").limit(2000);
  throwIfError(error);
  return excludeTestData(data)
    .filter((t) => !t.deleted_at)
    .map((t) => ({
      id: t.id,
      title: t.title,
      phase_id: t.phase_id,
      status: t.status,
      parent_task_id: t.parent_task_id
    }));
}

export async function getTask(id: string): Promise<Task> {
  const { data, error } = await supabase.from("tasks").select("*").eq("id", id).single();
  throwIfError(error);
  return assertData(data, "Task not found");
}

const TASK_INDEX_FIELDS = ["title", "description", "description_summary", "notes"] as const;

function shouldReindexTask(values: TaskInsert | TaskUpdate): boolean {
  return TASK_INDEX_FIELDS.some((k) => Object.prototype.hasOwnProperty.call(values, k));
}

function scheduleTaskIndex(task: Task): void {
  void indexTaskRow(task).catch(() => {
    // Indexelés best-effort — a mentés már sikerült.
  });
}

export async function createTask(values: TaskInsert, source: WriteSource = "ui"): Promise<Task> {
  const row = await insertWithWriteSource<Task>("tasks", values as unknown as Record<string, unknown>, source);
  scheduleTaskIndex(row);
  return row;
}

export async function updateTask(id: string, values: TaskUpdate): Promise<Task> {
  const { data, error } = await supabase
    .from("tasks")
    .update(values)
    .eq("id", id)
    .select("*")
    .single();
  throwIfError(error);
  const row = assertData(data, "Task not found");
  if (shouldReindexTask(values)) scheduleTaskIndex(row);
  return row;
}

/** Soft delete (F1.5). Undo: softRestoreTask. */
export async function deleteTask(id: string): Promise<void> {
  const { error } = await supabase
    .from("tasks")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", id);
  throwIfError(error);
}

export async function softRestoreTask(id: string): Promise<Task> {
  const { data, error } = await supabase
    .from("tasks")
    .update({ deleted_at: null })
    .eq("id", id)
    .select("*")
    .single();
  throwIfError(error);
  return assertData(data, "Task not found");
}

export async function reorderTasksWithinParent(args: {
  phaseId: string;
  parentTaskId: string | null;
  orderedIds: string[];
}): Promise<void> {
  const updates = args.orderedIds.map((id, idx) =>
    args.parentTaskId === null
      ? supabase
          .from("tasks")
          .update({ order_index: idx })
          .eq("id", id)
          .eq("phase_id", args.phaseId)
          .is("parent_task_id", null)
      : supabase
          .from("tasks")
          .update({ order_index: idx })
          .eq("id", id)
          .eq("phase_id", args.phaseId)
          .eq("parent_task_id", args.parentTaskId)
  );

  const results = await Promise.all(updates);
  for (const r of results) throwIfError(r.error);
}

export const tasksByPhaseQueryOptions = (phaseId: string) =>
  queryOptions({
    queryKey: [...qk.tasks, { phaseId }] as const,
    queryFn: () => listTasksByPhase(phaseId)
  });

export const taskQueryOptions = (id: string) =>
  queryOptions({
    queryKey: qk.task(id),
    queryFn: () => getTask(id),
    structuralSharing: false
  });

export const taskTreeQueryOptions = () =>
  queryOptions({
    queryKey: ["tasks", "tree"] as const,
    queryFn: listTaskTree
  });

export const tasksBriefQueryOptions = () =>
  queryOptions({
    queryKey: ["tasks", "briefPicker"] as const,
    queryFn: listTasksBrief
  });

/** Mai lista: folyamatban, vagy a tervezett nap átfedésben van a mával. */
export async function listOpenTasksForToday(today: string): Promise<Task[]> {
  const { data, error } = await supabase
    .from("tasks")
    .select("*")
    .in("status", ["planned", "in_progress", "blocked"])
    .order("order_index", { ascending: true })
    .limit(500);
  throwIfError(error);
  return excludeTestData(data)
    .filter((t) => isTaskForToday(t, today))
    .slice(0, 12);
}

export const todayTasksQueryOptions = (today: string) =>
  queryOptions({
    queryKey: ["tasks", "today", today] as const,
    queryFn: () => listOpenTasksForToday(today)
  });
